










































































import type { PropType } from '@nuxtjs/composition-api';
// eslint-disable-next-line no-duplicate-imports
import {
  computed,
  defineComponent,
  ref,
  useFetch
} from '@nuxtjs/composition-api';
import VaimoBanner from 'atoms/VaimoBanner.vue';
import { getButtonsWithSkuLinks } from 'templates/sections/EditoProductPush/getters/editoProductPushGetters';

import {
  useDeclination,
  useScreenSize,
  useVisual
} from '~/diptyqueTheme/composable';
import customProductGetters from '~/diptyqueTheme/helpers/productGetters';
import type { MediaPreloadableSection } from '~/diptyqueTheme/types/components';
import { ButtonTypeEnum } from '~/diptyqueTheme/types/contentful';
import type {
  ButtonModelFields,
  EditoProductPushModel
} from '~/integrations/contentful/src/types';
import { useProduct } from '~/modules/catalog/product/composables/useProduct';
import type { Product } from '~/modules/catalog/product/types';

export default defineComponent({
  name: 'EditoProductPush',
  components: {
    VaimoBanner,
    VaimoButtons: () => import('molecules/VaimoButtons.vue'),
    VaimoBadge: () => import('atoms/VaimoBadge.vue'),
    VaimoHeading: () => import('atoms/VaimoHeading.vue'),
    VaimoTruncatedText: () => import('atoms/VaimoTruncatedText.vue')
  },
  props: {
    sectionData: {
      required: true,
      type: Object as PropType<EditoProductPushModel & MediaPreloadableSection>
    },
    pageName: {
      type: String,
      required: false,
      default: ''
    },
    truncatedTextLength: {
      type: Number,
      required: false,
      default: 145
    }
  },
  setup(props) {
    const { getBannerOrVisualData } = useDeclination(props.sectionData);

    const { getProductList } = useProduct();

    const product = ref<Product | null>(null);

    const { isDesktop } = useScreenSize();

    /**
     * Default aspect ratios for mobile and desktop.
     * - Mobile legacy default is '3:4' ("Choose a Value")
     * - Mobile default is '3:4' ("Default for Current Module")
     * - Desktop legacy default is '3:4' ("Choose a Value")
     * - Desktop default is '16:9' ("Default for Current Module")
     */
    const DEFAULT_RATIO = {
      MOBILE: '3:4',
      MOBILE_LEGACY: '3:4',
      DESKTOP: '16:9',
      DESKTOP_LEGACY: '3:4'
    };

    const getProductInfo = async (sku: string): Promise<void> => {
      const data = await getProductList({
        filter: {
          sku: {
            in: [sku]
          }
        },
        pageSize: 1
      });

      if (data?.items?.length) {
        product.value = data.items[0] as Product;
      } else {
        product.value = null;
      }
    };

    const sku = computed<string>(() => props.sectionData?.sku || '');

    useFetch(() => sku.value && getProductInfo(sku.value));

    const buttons = computed<ButtonModelFields[]>(() =>
      getButtonsWithSkuLinks(props.sectionData, sku.value)
    );

    const hasButtons = computed<boolean>(() => {
      return buttons.value.length > 0;
    });

    /**
     * Checks if the provided ratio is a valid string in the format "X:Y".
     * Invalid ratios will default to the predefined values (e.g. "Default for current module").
     * @param ratio - The ratio string to validate
     * @returns {boolean} - Returns true if the ratio is valid, false otherwise
     */
    const isValidRatio = (ratio: string | undefined): boolean => {
      return typeof ratio === 'string' && /^\d+:\d+$/.test(ratio);
    };

    const { position } = useVisual(props.sectionData?.visualEntry);

    const aspectRatio = computed(() => {
      const { ratio, ratioD } = props.sectionData?.visualEntry || {};
      let componentRatio = {
        mobile: DEFAULT_RATIO.MOBILE,
        desktop: DEFAULT_RATIO.DESKTOP
      };

      // Use legacy defaults if ratio or ratioD are undefined
      if (typeof ratio === 'undefined') {
        componentRatio.mobile = DEFAULT_RATIO.MOBILE_LEGACY;
      } else if (isValidRatio(ratio)) {
        componentRatio.mobile = ratio;
      }

      if (typeof ratioD === 'undefined') {
        componentRatio.desktop = DEFAULT_RATIO.DESKTOP_LEGACY;
      } else if (isValidRatio(ratioD)) {
        componentRatio.desktop = ratioD;
      }

      return isDesktop.value ? componentRatio.desktop : componentRatio.mobile;
    });

    /**
     * Checks if the button is of "Quick buy" types.
     * @param button {ButtonModelFields} - Button model to check
     * @returns {boolean} - Returns true if the button is a "CTA Primary – Quick buy" type, otherwise false
     */
    const isQuickBuyCta = (button: ButtonModelFields): boolean => {
      const quickBuyTypes = {
        [ButtonTypeEnum.PrimaryQuickBuy]: true,
        [ButtonTypeEnum.PrimaryQuickBuyWhite]: true,
        [ButtonTypeEnum.PrimaryQuickBuyBlack]: true
      };

      return !!quickBuyTypes[button.type];
    };

    /**
     * Get section data with buttons and price
     * where buttons are mapped to include additional data for CTA Primary – Quick buy.
     * @param buttons {ButtonModelFields[]} - Array of button models
     * @param price {string | null} - Price to be added to CTA Primary – Quick buy
     */
    const getSectionData = (buttons: ButtonModelFields[], price = null) => ({
      buttonCollection: {
        items: buttons.map((button) => ({
          ...button,
          additionalData: isQuickBuyCta(button) ? price : null
        }))
      }
    });

    /**
     * Checks if there is a CTA Primary – Quick buy button in the buttons array.
     * Returns true if such a button exists, otherwise false.
     */
    const hasQuickBuyCta = computed(() => buttons.value.some(isQuickBuyCta));

    /**
     * Returns the price for CTA Primary – Quick buy.
     * If the price is 'N/A', it also returns null.
     * Otherwise, it returns the formatted price.
     *
     * @param product {Product | null} - Product object to get the price from
     */
    const getQuickBuyCtaPrice = (product: Product | null): string | null => {
      const price = customProductGetters.getProductCardPrice(product);

      if (price === 'N/A') {
        return null;
      }

      return price;
    };

    return {
      isDesktop,
      buttons,
      position,
      aspectRatio,
      hasButtons,
      hasQuickBuyCta,
      product,
      getBannerOrVisualData,
      customProductGetters,
      getSectionData,
      getQuickBuyCtaPrice
    };
  }
});
